<template>
  <div v-if="isVisible" class="custom-modal-overlay" @click="handleOverlayClick">
    <div class="custom-modal-content" @click.stop>
      <!-- Header -->
      <div class="modal-header">
        <div class="modal-icon">
          <span v-if="type === 'confirm'">❓</span>
          <span v-else-if="type === 'alert'">ℹ️</span>
          <span v-else-if="type === 'warning'">⚠️</span>
          <span v-else-if="type === 'error'">❌</span>
          <span v-else>ℹ️</span>
        </div>
        <h3 class="modal-title">{{ title || $t('common.confirm') }}</h3>
      </div>

      <!-- Content -->
      <div class="modal-body">
        <p class="modal-message">{{ message }}</p>
      </div>

      <!-- Actions -->
      <div class="modal-actions">
        <button 
          v-if="type === 'confirm'" 
          @click="handleCancel"
          class="modal-btn cancel-btn"
        >
          {{ cancelText || $t('common.cancel') }}
        </button>
        <button 
          @click="handleConfirm"
          class="modal-btn"
          :class="type === 'confirm' ? 'confirm-btn' : 'ok-btn'"
        >
          {{ confirmText || (type === 'confirm' ? $t('common.confirm') : 'OK') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const props = defineProps({
  type: {
    type: String,
    default: 'alert', // 'alert', 'confirm', 'warning', 'error'
  },
  title: {
    type: String,
    default: '' // Will use translation if empty
  },
  message: {
    type: String,
    required: true
  },
  confirmText: {
    type: String,
    default: '' // Will use translation if empty
  },
  cancelText: {
    type: String,
    default: '' // Will use translation if empty
  }
})

const emit = defineEmits(['confirm', 'cancel', 'close'])

const isVisible = ref(false)

function show() {
  isVisible.value = true
}

function hide() {
  isVisible.value = false
}

function handleConfirm() {
  emit('confirm')
  hide()
}

function handleCancel() {
  emit('cancel')
  hide()
}

function handleOverlayClick() {
  if (props.type === 'confirm') {
    handleCancel()
  } else {
    handleConfirm()
  }
}

// Expose methods for parent component
defineExpose({
  show,
  hide
})
</script>

<style scoped>
.custom-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.custom-modal-content {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  max-width: 400px;
  width: 90%;
  max-height: 80vh;
  overflow: auto;
  animation: modalSlideIn 0.3s ease;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 1.5rem 0 1.5rem;
}

.modal-icon {
  font-size: 2rem;
  flex-shrink: 0;
}

.modal-title {
  margin: 0;
  color: #1e293b;
  font-size: 1.25rem;
  font-weight: 600;
}

.modal-body {
  padding: 1rem 1.5rem;
}

.modal-message {
  color: #475569;
  line-height: 1.6;
  margin: 0;
  font-size: 1rem;
}

.modal-actions {
  display: flex;
  gap: 0.75rem;
  padding: 0 1.5rem 1.5rem 1.5rem;
  justify-content: flex-end;
}

.modal-btn {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;
}

.cancel-btn {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.cancel-btn:hover {
  background: #e2e8f0;
}

.confirm-btn {
  background: #667eea;
  color: white;
}

.confirm-btn:hover {
  background: #5a67d8;
}

.ok-btn {
  background: #10b981;
  color: white;
}

.ok-btn:hover {
  background: #059669;
}

/* Mobile responsive */
@media (max-width: 480px) {
  .custom-modal-content {
    width: 95%;
    margin: 1rem;
  }
  
  .modal-actions {
    flex-direction: column;
  }
  
  .modal-btn {
    width: 100%;
  }
}
</style>